import React, { useState, useEffect, useCallback, useContext } from "react";
import axios from "axios";
import { debounce } from "lodash";
import {
  Button,
  Form,
  Row,
  Col,
  Alert,
  Spinner,
  Modal,
  Card,
  ListGroup,
  Badge,
} from "react-bootstrap";
import AuthContext from "../context/AuthContext";

const API_URL = process.env.REACT_APP_API_URL;

const RosterManagement = () => {
  const { user } = useContext(AuthContext);
  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState("");
  const [rosters, setRosters] = useState([]);
  const [registrationSettings, setRegistrationSettings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [needsRefresh, setNeedsRefresh] = useState(false);
  const [teamSearchTerm, setTeamSearchTerm] = useState("");

  // Add Player Modal
  const [showAddPlayerModal, setShowAddPlayerModal] = useState(false);
  const [modalTeam, setModalTeam] = useState(null);
  const [unassignedPlayers, setUnassignedPlayers] = useState([]);
  const [playerSearchTerm, setPlayerSearchTerm] = useState("");
  const [loadingPlayers, setLoadingPlayers] = useState(false);

  // Penalty Modal
  const [showPenaltyModal, setShowPenaltyModal] = useState(false);
  const [penaltyPoints, setPenaltyPoints] = useState(0);

  const selectedTeamCanAddPlayers = (team) => {
    if (!user || !team) return false;
    if (user.role !== "manager") return true;
    return registrationSettings.some(
      (setting) =>
        Number(setting.level_team_id) === Number(team.level_team_id) &&
        Number(setting.manager_add_enabled) === 1,
    );
  };

  useEffect(() => {
    axios.get(`${API_URL}/seasons`).then((res) => {
      const seasonsData = res.data;
      setSeasons(seasonsData);
      if (seasonsData.length > 0) {
        setSelectedSeason(seasonsData[0].id);
      }
    });
  }, []);

  const fetchRosters = useCallback(
    debounce(async (season, searchTerm) => {
      if (!season || !user) {
        setRosters([]);
        return;
      }

      // For admin users, only fetch if a search term is provided.
      // Managers always fetch their own team.
      if (user.role !== "manager" && !searchTerm) {
        setRosters([]);
        return;
      }

      setLoading(true);
      try {
        let url;
        if (user.role === "manager") {
          // Manager chỉ fetch đội của mình
          url = `${API_URL}/rosters/${user.teamId}?season_id=${season}`;
        } else {
          // Admin fetch tất cả các đội (có tìm kiếm)
          url = `${API_URL}/rosters?season_id=${season}&search=${searchTerm}`;
        }
        const res = await axios.get(url);
        // Đảm bảo kết quả luôn là một mảng
        setRosters(Array.isArray(res.data) ? res.data : [res.data]);
      } catch (error) {
        console.error("Error fetching rosters:", error);
        setRosters([]); // Set rỗng nếu có lỗi
      } finally {
        setLoading(false);
      }
    }, 350),
    [user],
  );

  useEffect(() => {
    fetchRosters(selectedSeason, teamSearchTerm);
  }, [selectedSeason, teamSearchTerm, needsRefresh, fetchRosters]);

  useEffect(() => {
    if (!selectedSeason || !user) {
      setRegistrationSettings([]);
      return;
    }

    axios
      .get(`${API_URL}/rosters/registration-settings?season_id=${selectedSeason}`)
      .then((res) => setRegistrationSettings(res.data))
      .catch((error) => {
        console.error("Error fetching roster registration settings:", error);
        setRegistrationSettings([]);
      });
  }, [selectedSeason, user]);

  const fetchUnassignedPlayers = useCallback(
    debounce(async (currentSearchTerm) => {
      if (!selectedSeason) return;
      setLoadingPlayers(true);
      try {
        const res = await axios.get(
          `${API_URL}/rosters/unassigned-players?season_id=${selectedSeason}&search=${currentSearchTerm}`,
        );
        setUnassignedPlayers(res.data);
      } catch (error) {
        console.error("Error fetching unassigned players:", error);
      } finally {
        setLoadingPlayers(false);
      }
    }, 350),
    [selectedSeason],
  );

  useEffect(() => {
    if (showAddPlayerModal) {
      fetchUnassignedPlayers(playerSearchTerm);
    }
  }, [playerSearchTerm, showAddPlayerModal, fetchUnassignedPlayers]);

  const handleShowAddPlayer = (team) => {
    if (!selectedTeamCanAddPlayers(team)) return;
    setModalTeam(team);
    setPlayerSearchTerm("");
    setShowAddPlayerModal(true);
  };

  const handleAddPlayer = async (playerId) => {
    if (!selectedTeamCanAddPlayers(modalTeam)) return;
    await axios.post(`${API_URL}/rosters/add-player`, {
      season_id: selectedSeason,
      team_id: modalTeam.id,
      player_id: playerId,
    });
    setNeedsRefresh((p) => !p);
    fetchUnassignedPlayers(playerSearchTerm); // Refresh modal list
  };

  const handleRemovePlayer = async (teamId, playerId) => {
    if (window.confirm("Xóa VĐV khỏi đội hình?")) {
      await axios.delete(`${API_URL}/rosters/remove-player`, {
        data: {
          season_id: selectedSeason,
          team_id: teamId,
          player_id: playerId,
        },
      });
      setNeedsRefresh((p) => !p);
    }
  };

  const handleRoleChange = async (teamId, playerId, newRole) => {
    await axios.put(`${API_URL}/rosters/update-role`, {
      season_id: selectedSeason,
      team_id: teamId,
      player_id: playerId,
      team_role: newRole,
    });
    setNeedsRefresh((p) => !p);
  };

  const handleApplyPenalty = async () => {
    await axios.post(`${API_URL}/team-points/penalty`, {
      season_id: selectedSeason,
      team_id: modalTeam.id,
      level_team_id: modalTeam.level_team_id,
      points: penaltyPoints,
    });
    setShowPenaltyModal(false);
    setPenaltyPoints(0);
    // You might want to add a visual indicator for penalties later
  };

  const handleToggleRegistrationSetting = async (setting) => {
    const res = await axios.put(`${API_URL}/rosters/registration-settings`, {
      season_id: selectedSeason,
      level_team_id: setting.level_team_id,
      manager_add_enabled: Number(setting.manager_add_enabled) === 1 ? 0 : 1,
    });
    setRegistrationSettings(res.data);
  };

  return (
    <div>
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <h2 className="mb-0">Quản lý Đội hình Mùa giải</h2>
        {selectedSeason && user && user.role !== "manager" && (
          <small className="text-muted">Nhập tên đội để tải danh sách (giữ logic hiện tại)</small>
        )}
      </div>
      <Card className="shadow-sm border-0 mb-4">
        <Card.Body>
          <Form.Group as={Row} className="mb-3 align-items-center">
        <Form.Label column sm={2}>
          Chọn Mùa giải:
        </Form.Label>
        <Col sm={10}>
          <Form.Select
            value={selectedSeason}
            onChange={(e) => setSelectedSeason(e.target.value)}
          >
            <option value="">-- Chọn Mùa giải --</option>
            {seasons.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </Form.Select>
        </Col>
      </Form.Group>

      {selectedSeason && (
        <Form.Group as={Row} className="mb-4">
          <Col sm={12}>
            <Form.Control
              type="text"
              placeholder="Tìm kiếm đội..."
              value={teamSearchTerm}
              onChange={(e) => setTeamSearchTerm(e.target.value)}
            />
          </Col>
        </Form.Group>
      )}
        </Card.Body>
      </Card>

      {selectedSeason && user?.role === "admin" && (
        <Row className="g-4 mb-4">
          <Col lg={6}>
            <Card className="shadow-sm border-0 h-100">
              <Card.Header className="fw-semibold">Bật thêm VĐV theo serie</Card.Header>
              <Card.Body>
                {registrationSettings.length === 0 ? (
                  <Alert variant="info" className="mb-0">Chưa có dữ liệu serie.</Alert>
                ) : (
                  registrationSettings.map((setting) => (
                    <div
                      key={setting.level_team_id}
                      className="d-flex justify-content-between align-items-center border-bottom py-2"
                    >
                      <span>{setting.level_team_name}</span>
                      <Form.Check
                        type="switch"
                        id={`roster-registration-${setting.level_team_id}`}
                        checked={Number(setting.manager_add_enabled) === 1}
                        onChange={() => handleToggleRegistrationSetting(setting)}
                        label={Number(setting.manager_add_enabled) === 1 ? "Đang bật" : "Đang tắt"}
                      />
                    </div>
                  ))
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {selectedSeason && user?.role === "manager" && rosters[0] && !selectedTeamCanAddPlayers(rosters[0]) && (
        <Alert variant="warning" className="shadow-sm border-0">
          Admin chưa bật chức năng thêm VĐV cho serie của đội bạn. Bạn vẫn có thể chỉnh vai trò/xóa VĐV hiện có.
        </Alert>
      )}

      {loading && (
        <div className="text-center my-4">
          <Spinner animation="border" role="status" />
        </div>
      )}

      {!loading && selectedSeason && user && user.role !== "manager" && !teamSearchTerm && rosters.length === 0 && (
        <Alert variant="info" className="text-center shadow-sm border-0">
          Vui lòng nhập tên đội vào ô tìm kiếm để xem đội hình.
        </Alert>
      )}

      {/* Admin View: Grid Layout */}
      {!loading && selectedSeason && user && user.role !== "manager" && (
        <Row xs={1} md={2} xl={3} className="g-4">
          {rosters.map((team) => (
            <Col key={team.id}>
              <Card className="h-100 shadow-sm border-0">
                <Card.Header
                  as="h5"
                  className="d-flex justify-content-between align-items-center gap-2"
                >
                  <div className="fw-semibold text-truncate" title={team.name}>{team.name}</div>
                  <Badge bg="primary" pill>
                    {team.players.length} VĐV
                  </Badge>
                </Card.Header>
                <Card.Body style={{ maxHeight: "340px", overflowY: "auto" }}>
                  <ListGroup variant="flush">
                    {team.players.map((player) => (
                      <ListGroup.Item
                        key={player.id}
                        className="d-flex justify-content-between align-items-center gap-2"
                      >
                        <div className="text-truncate" title={player.name} style={{ maxWidth: "42%" }}>{player.name}</div>
                        <div className="d-flex align-items-center gap-2">
                          <Form.Select
                            size="sm"
                            value={player.team_role}
                            onChange={(e) =>
                              handleRoleChange(
                                team.id,
                                player.id,
                                e.target.value,
                              )
                            }
                            style={{ width: "118px" }}
                          >
                            <option value="1">Thành viên</option>
                            <option value="2">Đội phó</option>
                            <option value="3">Đội trưởng</option>
                          </Form.Select>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() =>
                              handleRemovePlayer(team.id, player.id)
                            }
                          >
                            Xóa
                          </Button>
                        </div>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </Card.Body>
                <Card.Footer className="d-flex justify-content-end gap-2 bg-white">
                  <Button
                    variant="success"
                    size="sm"
                    onClick={() => handleShowAddPlayer(team)}
                  >
                    Thêm VĐV
                  </Button>
                  {user && user.role === "admin" && (
                    <Button
                      variant="warning"
                      size="sm"
                      onClick={() => {
                        setModalTeam(team);
                        setShowPenaltyModal(true);
                      }}
                    >
                      Phạt Điểm
                    </Button>
                  )}
                </Card.Footer>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* Manager View: Single, larger card */}
      {!loading &&
        selectedSeason &&
        user &&
        user.role === "manager" &&
        rosters.length > 0 && (
          <Row>
            <Col md={9} className="mx-auto">
              {rosters.map((team) => (
                <Card key={team.id} className="shadow-sm border-0">
                  <Card.Header
                    as="h5"
                    className="d-flex justify-content-between align-items-center gap-2"
                  >
                    {team.name}
                    <Badge bg="primary" pill>
                      {team.players.length} VĐV
                    </Badge>
                  </Card.Header>
                  <Card.Body style={{ maxHeight: "420px", overflowY: "auto" }}>
                    <ListGroup variant="flush">
                      {team.players.map((player) => (
                        <ListGroup.Item
                          key={player.id}
                          className="d-flex justify-content-between align-items-center gap-2"
                        >
                          <div className="text-truncate" title={player.name} style={{ maxWidth: "42%" }}>{player.name}</div>
                          <div className="d-flex align-items-center gap-2">
                            <Form.Select
                              size="sm"
                              value={player.team_role}
                              onChange={(e) =>
                                handleRoleChange(
                                  team.id,
                                  player.id,
                                  e.target.value,
                                )
                              }
                              className="me-2"
                              style={{ width: "120px" }}
                            >
                              <option value="1">Thành viên</option>
                              <option value="2">Đội phó</option>
                              <option value="3">Đội trưởng</option>
                            </Form.Select>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() =>
                                handleRemovePlayer(team.id, player.id)
                              }
                            >
                              Xóa
                            </Button>
                          </div>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  </Card.Body>
                  {selectedTeamCanAddPlayers(team) && (
                    <Card.Footer className="d-flex justify-content-end gap-2 bg-white">
                      <Button
                        variant="success"
                        onClick={() => handleShowAddPlayer(team)}
                      >
                        Thêm VĐV
                      </Button>
                    </Card.Footer>
                  )}
                </Card>
              ))}
            </Col>
          </Row>
        )}

      {/* Add Player Modal */}
      {modalTeam && selectedTeamCanAddPlayers(modalTeam) && (
        <Modal
          show={showAddPlayerModal}
          onHide={() => setShowAddPlayerModal(false)}
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>Thêm VĐV cho đội {modalTeam?.name}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Control
              type="text"
              placeholder="Tìm kiếm VĐV..."
              value={playerSearchTerm}
              onChange={(e) => setPlayerSearchTerm(e.target.value)}
              className="mb-3"
            />
            {loadingPlayers ? (
              <Spinner />
            ) : (
              <ListGroup>
                {unassignedPlayers.map((player) => (
                  <ListGroup.Item
                    key={player.id}
                    className="d-flex justify-content-between align-items-center gap-2"
                  >
                    <div className="text-truncate" title={player.name} style={{ maxWidth: "42%" }}>{player.name}</div>
                    <div className="d-flex gap-2">
                      <Button
                        size="sm"
                        variant="outline-info"
                        as="a"
                        href={`https://bongbanhadong.com/players/${player.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Xem profile để xác thực VĐV"
                      >
                        👁️ Xem
                      </Button>
                      <Button
                        size="sm"
                        variant="success"
                        onClick={() => handleAddPlayer(player.id)}
                      >
                        ➕ Thêm
                      </Button>
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}
          </Modal.Body>
        </Modal>
      )}

      {/* Penalty Modal */}
      <Modal show={showPenaltyModal} onHide={() => setShowPenaltyModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Phạt điểm đội {modalTeam?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Nhập số điểm muốn phạt (ví dụ: 10)</Form.Label>
            <Form.Control
              type="number"
              value={penaltyPoints}
              onChange={(e) => setPenaltyPoints(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowPenaltyModal(false)}
          >
            Hủy
          </Button>
          <Button variant="warning" onClick={handleApplyPenalty}>
            Áp dụng Phạt
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default RosterManagement;
